import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
} from "react";
import LanguageSelection from "../LanguageSelection/LanguageSelection";

const FromToLanguage = forwardRef(
  (
    {
      onLanguageSelectionChanged,
      fromStorageKey,
      toStorageKey,
      selectionRequired,
    },
    ref
  ) => {
    const fromRef = useRef(null);
    const toRef = useRef(null);
    const [currentFromLanguage, setCurrentFromLanguage] = useState(null);
    const [currentFromLanguageState, setCurrentFromLanguageState] =
      useState(false);
    const [currentToLanguage, setCurrentToLanguage] = useState(null);
    const [currentToLanguageState, setCurrentToLanguageState] = useState(false);

    useEffect(() => {
      console.log(
        "FromToLanguage mounted, initializing states from localStorage"
      );

      // Load "From" language
      const storedFromVisible = JSON.parse(
        localStorage.getItem(fromStorageKey) || "[]"
      );
      console.log(`Reading ${fromStorageKey}:`, storedFromVisible);
      const storedFromSelected = JSON.parse(
        localStorage.getItem(`${fromStorageKey}_selected`) || "null"
      );
      console.log(`Reading ${fromStorageKey}_selected:`, storedFromSelected);
      const initialFromLanguage =
        storedFromSelected && storedFromSelected.id && storedFromSelected.name
          ? storedFromSelected
          : null;
      const initialFromState = !!initialFromLanguage;

      // Load "To" language
      const storedToVisible = JSON.parse(
        localStorage.getItem(toStorageKey) || "[]"
      );
      console.log(`Reading ${toStorageKey}:`, storedToVisible);
      const storedToSelected = JSON.parse(
        localStorage.getItem(`${toStorageKey}_selected`) || "null"
      );
      console.log(`Reading ${toStorageKey}_selected:`, storedToSelected);
      const initialToLanguage =
        storedToSelected && storedToSelected.id && storedToSelected.name
          ? storedToSelected
          : null;
      const initialToState = !!initialToLanguage;

      setCurrentFromLanguage(initialFromLanguage);
      setCurrentFromLanguageState(initialFromState);
      setCurrentToLanguage(initialToLanguage);
      setCurrentToLanguageState(initialToState);

      // Initialize refs
      if (fromRef.current) {
        fromRef.current.setLanguage(initialFromLanguage);
      }
      if (toRef.current) {
        toRef.current.setLanguage(initialToLanguage);
      }
    }, [fromStorageKey, toStorageKey]);

    const handleFromSelectionChanged = (language) => {
      console.log("From selection attempt:", language, {
        currentFromLanguage,
        currentToLanguage,
      });

      const newState = !!language;
      if (
        currentToLanguage &&
        language &&
        language.id === currentToLanguage.id
      ) {
        console.log("Blocked: From language matches To language");
        alert(
          "The 'From' and 'To' languages cannot be the same. Please choose a different language."
        );
        if (fromRef.current) {
          fromRef.current.setLanguage(currentFromLanguage);
        }
        return;
      }

      console.log("Valid From selection:", language, "State:", newState);
      setCurrentFromLanguage(language);
      setCurrentFromLanguageState(newState);
      if (onLanguageSelectionChanged) {
        console.log("Notifying parent of From change:", {
          ...language,
          type: "from",
        });
        onLanguageSelectionChanged({
          ...(language || {
            id: null,
            name: null,
            number_of_speakers: null,
            location: null,
            code: null,
            flag: null,
          }),
          type: "from",
        });
      }
    };

    const handleToSelectionChanged = (language) => {
      console.log("To selection attempt:", language, {
        currentFromLanguage,
        currentToLanguage,
      });

      const newState = !!language;
      if (
        currentFromLanguage &&
        language &&
        language.id === currentFromLanguage.id
      ) {
        console.log("Blocked: To language matches From language");
        alert(
          "The 'From' and 'To' languages cannot be the same. Please choose a different language."
        );
        if (toRef.current) {
          toRef.current.setLanguage(currentToLanguage);
        }
        return;
      }

      console.log("Valid To selection:", language, "State:", newState);
      setCurrentToLanguage(language);
      setCurrentToLanguageState(newState);
      if (onLanguageSelectionChanged) {
        console.log("Notifying parent of To change:", {
          ...language,
          type: "to",
        });
        onLanguageSelectionChanged({
          ...(language || {
            id: null,
            name: null,
            number_of_speakers: null,
            location: null,
            code: null,
            flag: null,
          }),
          type: "to",
        });
      }
    };

    useImperativeHandle(ref, () => ({
      getFromLanguage: () => currentFromLanguage,
      getToLanguage: () => currentToLanguage,
    }));

    return (
      <div className="from-to-language max-w-5xl mx-auto p-5 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
          <label className="text-lg font-semibold text-gray-700 w-full sm:w-32">
            From Language:
            {selectionRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex-1">
            <LanguageSelection
              ref={fromRef}
              configPath="/HomeLanguageSelection/fromConfig.json"
              onLanguageSelectionChanged={handleFromSelectionChanged}
              localStorageKey={fromStorageKey}
              aria-label="Select source language"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
          <label className="text-lg font-semibold text-gray-700 w-full sm:w-32">
            To Language:
            {selectionRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex-1">
            <LanguageSelection
              ref={toRef}
              configPath="/HomeLanguageSelection/toConfig.json"
              onLanguageSelectionChanged={handleToSelectionChanged}
              localStorageKey={toStorageKey}
              aria-label="Select target language for revenue estimate"
            />
          </div>
        </div>
      </div>
    );
  }
);

export default FromToLanguage;
